/**
 * Video Reels Slider Engine
 *
 * FIX 1 — BG Videos keep playing while modal is open.
 *          Only the slider glider is paused; IntersectionObserver
 *          continues to play/pause cards based on viewport visibility.
 *
 * FIX 2 — Modal prev/next navigation is NOW inside the player box,
 *          perfectly circular, always visible and clickable.
 *
 * Sizing:
 *  Desktop  >= 992px : 5-col  =  (viewport - 48px padding - 4*18px gap) / 5
 *  Mobile   <= 991px : fixed 220px card  /  380px video height
 *  Product bar       : min-height 68px  |  padding 12px 14px  |  thumb 48x48
 */

class VideoReelsSlider {
  constructor(sectionElement) {
    this.section = sectionElement;
    this.sectionId = this.section.dataset.sectionId;
    this.viewport = this.section.querySelector('[data-reels-viewport]');
    this.track = this.section.querySelector('[data-reels-track]');
    if (!this.viewport || !this.track) return;

    this.originalCards = Array.from(this.track.querySelectorAll('.video-reel-card'));
    this.totalOriginal = this.originalCards.length;
    if (this.totalOriginal === 0) return;

    // Settings
    this.isAutoplay   = this.section.dataset.autoplay   === 'true';
    this.pauseOnHover = this.section.dataset.pauseHover === 'true';
    this.enableGlide  = this.section.dataset.enableGlide === 'true';

    const speedSetting = parseFloat(this.section.dataset.speed || '55');
    this.speed = Math.max(0.3, Math.min(2.5, 45 / speedSetting));

    // Glider state
    this.currentTranslate = 0;
    this.singleSetWidth   = 0;
    this.isGliderRunning  = false;
    this.isPaused         = false;
    this.rafId            = null;
    this.isNavigating     = false;

    // Drag state
    this.isDragging         = false;
    this.dragStartX         = 0;
    this.dragStartTranslate = 0;
    this.hasMoved           = false;
    this.resumeTimeout      = null;

    // Modal state
    this.modal             = this.section.querySelector('[data-reels-modal]');
    this.modalVideo        = this.modal ? this.modal.querySelector('[data-modal-video]') : null;
    this.currentModalIndex = 0;
    this.isModalOpen       = false;

    // Observer
    this.intersectionObserver = null;

    this.init();
  }

  init() {
    this.calculateCardWidths();
    this.setupInfiniteClones();
    this.setupIntersectionObserver();
    this.setupDragEvents();
    this.setupHoverAndTouch();
    this.setupArrowButtons();
    this.setupModal();
    this.setupResizeListener();
    if (this.enableGlide) this.startGlider();
  }

  /* ------------------------------------------------------------------
     EXACT 5-CARD ROW  (desktop)
     Formula: (viewport - 48 - 4*18) / 5
  ------------------------------------------------------------------ */
  calculateCardWidths() {
    if (window.innerWidth >= 992) {
      const vw    = this.viewport.clientWidth;
      const cardW = Math.floor((vw - 48 - 4 * 18) / 5);
      const final = Math.max(200, cardW > 0 ? cardW : 290);
      this.track.style.setProperty('--pc-5-col-width', `${final}px`);
      this.section.style.setProperty('--pc-5-col-width', `${final}px`);
    } else {
      this.track.style.removeProperty('--pc-5-col-width');
      this.section.style.removeProperty('--pc-5-col-width');
    }
    this.calculateTrackMetrics();
  }

  calculateTrackMetrics() {
    const style = window.getComputedStyle(this.track);
    const gap   = parseFloat(style.gap) || (window.innerWidth >= 992 ? 18 : 14);
    let total   = 0;
    this.originalCards.forEach(c => { total += c.offsetWidth + gap; });
    this.singleSetWidth = total;
  }

  setupInfiniteClones() {
    if (!this.enableGlide || this.totalOriginal < 2) return;
    if (this.track.dataset.cloned === 'true') return;

    const frag = document.createDocumentFragment();
    this.originalCards.forEach(card => {
      const clone = card.cloneNode(true);
      clone.classList.add('is-clone');
      clone.setAttribute('data-is-clone', 'true');
      frag.appendChild(clone);
    });
    this.track.appendChild(frag);
    this.track.dataset.cloned = 'true';
    this.calculateTrackMetrics();
  }

  /* ------------------------------------------------------------------
     SMART INTERSECTION OBSERVER
     NOTE: No isModalOpen guard — BG videos keep playing based purely
     on physical viewport visibility, even when modal is open.
  ------------------------------------------------------------------ */
  setupIntersectionObserver() {
    if (!this.isAutoplay) return;

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const video = entry.target.querySelector('video.reel-stream-video');
        if (!video) return;
        // FIX 1: modal state is irrelevant — only viewport intersection matters
        if (entry.isIntersecting && entry.intersectionRatio >= 0.25) {
          this.playPreviewVideo(video);
        } else {
          this.pausePreviewVideo(video);
        }
      });
    }, {
      root: null,
      rootMargin: '40px 0px 40px 0px',
      threshold: [0, 0.25, 0.5]
    });

    this.track.querySelectorAll('.video-reel-card').forEach(c =>
      this.intersectionObserver.observe(c)
    );
  }

  playPreviewVideo(video) {
  if (!video || !video.paused) return;
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;

  // Mobile Fix: iOS ignores preload, so force buffer before play
  if (video.readyState < 2) {
    video.load();
    video.addEventListener('canplay', () => {
      video.play().catch(() => {});
    }, { once: true });
  } else {
    video.play().catch(() => {});
  }
}

  pausePreviewVideo(video) {
    if (video && !video.paused) video.pause();
  }

  pauseAllPreviewVideos() {
    this.track.querySelectorAll('video.reel-stream-video').forEach(v =>
      this.pausePreviewVideo(v)
    );
  }

  resumeVisiblePreviewVideos() {
    if (!this.isAutoplay) return;
    this.track.querySelectorAll('.video-reel-card').forEach(card => {
      const rect   = card.getBoundingClientRect();
      const inView = rect.right > 0 && rect.left < window.innerWidth;
      const video  = card.querySelector('video.reel-stream-video');
      if (!video) return;
      if (inView) this.playPreviewVideo(video);
      else        this.pausePreviewVideo(video);
    });
  }

  /* ------------------------------------------------------------------
     GLIDER
  ------------------------------------------------------------------ */
  startGlider() {
    this.isGliderRunning = true;
    const tick = () => {
      if (!this.isGliderRunning) return;
      if (!this.isPaused && !this.isDragging && !this.isNavigating && this.singleSetWidth > 0) {
        this.currentTranslate -= this.speed;
        if (Math.abs(this.currentTranslate) >= this.singleSetWidth) {
          this.currentTranslate += this.singleSetWidth;
        } else if (this.currentTranslate > 0) {
          this.currentTranslate -= this.singleSetWidth;
        }
        this.applyTransform(this.currentTranslate);
      }
      this.rafId = requestAnimationFrame(tick);
    };
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = requestAnimationFrame(tick);
  }

  applyTransform(v) {
    this.track.style.transform = `translate3d(${v}px, 0, 0)`;
  }

  /* ------------------------------------------------------------------
     SLIDER ARROW BUTTONS
  ------------------------------------------------------------------ */
  setupArrowButtons() {
    const prev = this.section.querySelector('[data-reels-prev]');
    const next = this.section.querySelector('[data-reels-next]');
    if (!prev && !next) return;

    if (prev) prev.addEventListener('click', e => { e.preventDefault(); this.navigateStep(1);  });
    if (next) next.addEventListener('click', e => { e.preventDefault(); this.navigateStep(-1); });

    this.viewport.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); this.navigateStep(1);  }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.navigateStep(-1); }
    });
  }

  navigateStep(direction) {
    if (this.isNavigating) return;
    this.isNavigating = true;
    this.isPaused = true;
    clearTimeout(this.resumeTimeout);

    const style = window.getComputedStyle(this.track);
    const gap   = parseFloat(style.gap) || (window.innerWidth >= 992 ? 18 : 14);
    const step  = (this.originalCards[0] ? this.originalCards[0].offsetWidth : 280) + gap;
    let target  = this.currentTranslate + direction * step;

    if (direction < 0 && Math.abs(target) >= this.singleSetWidth && this.singleSetWidth > 0) {
      this.currentTranslate += this.singleSetWidth;
      target                += this.singleSetWidth;
      this.track.style.transition = 'none';
      this.applyTransform(this.currentTranslate);
      this.track.offsetHeight;
    } else if (direction > 0 && target > 0 && this.singleSetWidth > 0) {
      this.currentTranslate -= this.singleSetWidth;
      target                -= this.singleSetWidth;
      this.track.style.transition = 'none';
      this.applyTransform(this.currentTranslate);
      this.track.offsetHeight;
    }

    this.track.style.transition = 'transform 0.42s cubic-bezier(0.22, 1, 0.36, 1)';
    this.currentTranslate = target;
    this.applyTransform(this.currentTranslate);

    setTimeout(() => {
      this.track.style.transition = '';
      this.isNavigating = false;
      this.resumeVisiblePreviewVideos();
      this.resumeTimeout = setTimeout(() => { this.isPaused = false; }, 3500);
    }, 420);
  }

  /* ------------------------------------------------------------------
     DRAG & TOUCH
  ------------------------------------------------------------------ */
  setupDragEvents() {
    const onStart = clientX => {
      this.isDragging = true;
      this.hasMoved   = false;
      this.dragStartX         = clientX;
      this.dragStartTranslate = this.currentTranslate;
      this.viewport.classList.add('is-dragging');
      this.track.style.transition = '';
      clearTimeout(this.resumeTimeout);
    };

    const onMove = clientX => {
      if (!this.isDragging) return;
      const diffX = clientX - this.dragStartX;
      if (Math.abs(diffX) > 6) this.hasMoved = true;
      this.currentTranslate = this.dragStartTranslate + diffX;
      if (this.singleSetWidth > 0) {
        if (Math.abs(this.currentTranslate) >= this.singleSetWidth) {
          this.currentTranslate   += this.singleSetWidth;
          this.dragStartTranslate += this.singleSetWidth;
        } else if (this.currentTranslate > 0) {
          this.currentTranslate   -= this.singleSetWidth;
          this.dragStartTranslate -= this.singleSetWidth;
        }
      }
      this.applyTransform(this.currentTranslate);
    };

    const onEnd = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      this.viewport.classList.remove('is-dragging');
      this.resumeVisiblePreviewVideos();
      this.resumeTimeout = setTimeout(() => {
        this.hasMoved = false;
        this.isPaused = false;
      }, 1500);
    };

    this.viewport.addEventListener('mousedown',  e => { if (e.button === 0) onStart(e.clientX); });
    window.addEventListener('mousemove',         e => { if (this.isDragging) onMove(e.clientX); });
    window.addEventListener('mouseup',           ()  => { if (this.isDragging) onEnd(); });
    this.viewport.addEventListener('touchstart', e => { if (e.touches.length === 1) onStart(e.touches[0].clientX); }, { passive: true });
    this.viewport.addEventListener('touchmove',  e => { if (this.isDragging && e.touches.length === 1) onMove(e.touches[0].clientX); }, { passive: true });
    this.viewport.addEventListener('touchend',   ()  => { onEnd(); }, { passive: true });
  }

  setupHoverAndTouch() {
    if (!this.pauseOnHover) return;
    this.viewport.addEventListener('mouseenter', () => { if (!this.isModalOpen) this.isPaused = true; });
    this.viewport.addEventListener('mouseleave', () => { if (!this.isModalOpen && !this.isDragging && !this.isNavigating) this.isPaused = false; });
  }

  /* ------------------------------------------------------------------
     MODAL
     FIX 1: openModal does NOT call pauseAllPreviewVideos.
             BG videos managed solely by IntersectionObserver.
     FIX 2: prev/next buttons are inside reels-modal-player-box (liquid),
             always visible as solid circles.
  ------------------------------------------------------------------ */
  setupModal() {
    if (!this.modal) return;

    const closeBtns    = this.modal.querySelectorAll('[data-modal-close]');
    const soundBtn     = this.modal.querySelector('[data-modal-sound]');
    const playerBox    = this.modal.querySelector('[data-modal-player]');
    const modalPrevBtn = this.modal.querySelector('[data-modal-prev]');
    const modalNextBtn = this.modal.querySelector('[data-modal-next]');

    // Open on card click
    this.track.addEventListener('click', e => {
      if (this.hasMoved) { this.hasMoved = false; return; }
      const card = e.target.closest('.video-reel-card');
      if (!card) return;
      const idx = parseInt(card.dataset.index, 10) % this.totalOriginal || 0;
      this.openModal(idx);
    });

    // Close
    closeBtns.forEach(btn => btn.addEventListener('click', e => {
      e.stopPropagation();
      this.closeModal();
    }));

    // Sound
    if (soundBtn && this.modalVideo) {
      soundBtn.addEventListener('click', e => { e.stopPropagation(); this.toggleModalSound(); });
    }

    // Circular modal nav (FIX 2)
    if (modalPrevBtn) modalPrevBtn.addEventListener('click', e => { e.stopPropagation(); this.navigateModal(-1); });
    if (modalNextBtn) modalNextBtn.addEventListener('click', e => { e.stopPropagation(); this.navigateModal(1);  });

    // Tap to pause/play
    if (playerBox && this.modalVideo) {
      playerBox.addEventListener('click', e => {
        if (e.target.closest('.reels-modal-product-drawer') ||
            e.target.closest('.reels-modal-top-bar') ||
            e.target.closest('.modal-nav-btn')) return;
        this.toggleModalPlayPause();
      });
    }

    // Progress bar
    if (this.modalVideo) {
      this.modalVideo.addEventListener('timeupdate', () => {
        if (!this.modalVideo.duration) return;
        const pct  = (this.modalVideo.currentTime / this.modalVideo.duration) * 100;
        const fill = this.modal.querySelector('[data-modal-progress]');
        if (fill) fill.style.width = `${pct}%`;
      });
    }

    // Keyboard
    window.addEventListener('keydown', e => {
      if (!this.isModalOpen) return;
      if (e.key === 'Escape')      this.closeModal();
      else if (e.key === 'ArrowLeft')  this.navigateModal(-1);
      else if (e.key === 'ArrowRight') this.navigateModal(1);
      else if (e.key === ' ' || e.code === 'Space') { e.preventDefault(); this.toggleModalPlayPause(); }
    });

    // Touch swipe inside modal
    let tx = 0, ty = 0;
    this.modal.addEventListener('touchstart', e => {
      if (e.touches.length === 1) { tx = e.touches[0].clientX; ty = e.touches[0].clientY; }
    }, { passive: true });
    this.modal.addEventListener('touchend', e => {
      if (e.changedTouches.length !== 1) return;
      const dx = e.changedTouches[0].clientX - tx;
      const dy = e.changedTouches[0].clientY - ty;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) this.navigateModal(1); else this.navigateModal(-1);
      } else if (Math.abs(dy) > 80) {
        if (dy < 0) this.navigateModal(1); else this.navigateModal(-1);
      }
    }, { passive: true });
  }

  openModal(index) {
    if (!this.modal || !this.modalVideo) return;
    const card = this.originalCards[index];
    if (!card) return;

    this.isModalOpen = true;
    this.currentModalIndex = index;

    // FIX 1: Only pause the glider — BG videos are NOT touched.
    //        IntersectionObserver keeps handling them independently.
    this.isPaused = true;

    // Load & play modal video
    const videoUrl = card.dataset.videoUrl;
    if (videoUrl) {
      this.modalVideo.src = videoUrl;
      this.modalVideo.currentTime = 0;
      this.modalVideo.muted = false;
      this.modalVideo.play().catch(() => {
        this.modalVideo.muted = true;
        this.updateSoundIcons(true);
        this.modalVideo.play().catch(() => {});
      });
      this.updateSoundIcons(this.modalVideo.muted);
    }

    this.populateModalProduct(card);
    this.modal.classList.add('is-active');
    this.modal.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
  }

  populateModalProduct(card) {
    const box = this.modal.querySelector('[data-modal-product-box]');
    if (!box) return;

    const title = card.dataset.productTitle || card.dataset.title;
    const price = card.dataset.productPrice;
    const url   = card.dataset.productUrl;
    const img   = card.dataset.productImage;

    if (title && (url || price)) {
      box.style.display = 'flex';
      const tElem = this.modal.querySelector('[data-modal-product-title]');
      const pElem = this.modal.querySelector('[data-modal-product-price]');
      const iElem = this.modal.querySelector('[data-modal-product-img]');
      const lElem = this.modal.querySelector('[data-modal-product-link]');
      if (tElem) tElem.textContent = title;
      if (pElem) pElem.textContent = price || '';
      if (iElem) { if (img) { iElem.src = img; iElem.style.display = 'block'; } else { iElem.style.display = 'none'; } }
      if (lElem && url) lElem.href = url;
    } else {
      box.style.display = 'none';
    }
  }

  navigateModal(direction) {
    let next = (this.currentModalIndex + direction) % this.totalOriginal;
    if (next < 0) next = this.totalOriginal - 1;
    this.openModal(next);
  }

  toggleModalSound() {
    if (!this.modalVideo) return;
    this.modalVideo.muted = !this.modalVideo.muted;
    this.updateSoundIcons(this.modalVideo.muted);
  }

  updateSoundIcons(isMuted) {
    const btn = this.modal.querySelector('[data-modal-sound]');
    if (!btn) return;
    const m  = btn.querySelector('.icon-muted');
    const um = btn.querySelector('.icon-unmuted');
    if (!m || !um) return;
    m.style.display  = isMuted ? 'block' : 'none';
    um.style.display = isMuted ? 'none'  : 'block';
  }

  toggleModalPlayPause() {
    if (!this.modalVideo) return;
    const ind = this.modal.querySelector('[data-modal-play-indicator]');
    if (this.modalVideo.paused) {
      this.modalVideo.play().then(() => { if (ind) ind.classList.remove('show'); }).catch(() => {});
    } else {
      this.modalVideo.pause();
      if (ind) ind.classList.add('show');
    }
  }

  closeModal() {
    if (!this.modal) return;
    this.isModalOpen = false;
    this.modal.classList.remove('is-active');
    this.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';

    if (this.modalVideo) {
      this.modalVideo.pause();
      this.modalVideo.src = '';
    }

    const fill = this.modal.querySelector('[data-modal-progress]');
    if (fill) fill.style.width = '0%';

    // Resume glider; BG videos already managed by observer
    this.isPaused = false;
  }

  /* ------------------------------------------------------------------
     RESIZE & DESTROY
  ------------------------------------------------------------------ */
  setupResizeListener() {
    let t;
    window.addEventListener('resize', () => {
      clearTimeout(t);
      t = setTimeout(() => {
        this.calculateCardWidths();
        this.resumeVisiblePreviewVideos();
      }, 180);
    });
  }

  destroy() {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    if (this.intersectionObserver) this.intersectionObserver.disconnect();
    this.pauseAllPreviewVideos();
    if (this.modalVideo) { this.modalVideo.pause(); this.modalVideo.src = ''; }
  }
}

/* Boot */
function initVideoReels() {
  document.querySelectorAll('[data-reels-section]').forEach(sec => {
    if (!sec._reelsInstance) sec._reelsInstance = new VideoReelsSlider(sec);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVideoReels);
} else {
  initVideoReels();
}

if (window.Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', e => {
    const s = e.target.querySelector('[data-reels-section]');
    if (s) s._reelsInstance = new VideoReelsSlider(s);
  });
  document.addEventListener('shopify:section:unload', e => {
    const s = e.target.querySelector('[data-reels-section]');
    if (s && s._reelsInstance) { s._reelsInstance.destroy(); delete s._reelsInstance; }
  });
}